import React, { useEffect, useState, useCallback } from "react";
import {
    Box, Typography, TextField, MenuItem, Button, IconButton, Tooltip, FormHelperText, Autocomplete,
    Menu, ListItemIcon, ListItemText, Chip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import segmentService from "../../../services/segmentService";
import {
    UNOMI_COMPARISON_OPERATORS,
    buildUnomiConditionFromRows,
    defaultEmptyRows,
    DEFAULT_UNOMI_PROPERTY_FIELDS,
    normalizePropertyId,
    isKnownProperty,
    toDateInputValue,
    reconcileRowJoins,
} from "../utils/unomiConditionUtils";

const genId = () => `u_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

/** Depth-based tints — mirrors fidelite ConditionBuilder for nested group readability. */
const GROUP_DEPTH_BG = [
    "#eef9ff", // level 0 – light sky
    "#fefce8", // level 1 – light amber
    "#f0fdf4", // level 2 – light mint
    "#fdf4ff", // level 3 – light violet
    "#fff1f2", // level 4 – light rose
];
const GROUP_DEPTH_ACCENT = ["#007B8A", "#d97706", "#059669", "#7c3aed", "#e11d48"];

function groupDepthStyle(depth) {
    const level = Math.min(depth, GROUP_DEPTH_BG.length - 1);
    const accent = GROUP_DEPTH_ACCENT[level];
    return {
        level,
        accent,
        bg: GROUP_DEPTH_BG[level],
    };
}

const selectMenuProps = {
    PaperProps: {
        style: { maxHeight: 320 },
        sx: { zIndex: 1600 },
    },
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    disableScrollLock: true,
};

const makeCondition = (fields) => ({
    id: genId(),
    type: "condition",
    property: fields?.[0]?.id || "",
    operator: "equals",
    value: "",
});

const makeGroup = (fields) => ({
    id: genId(),
    type: "group",
    conditions: [makeCondition(fields)],
});

/** ET / OU selector shown between consecutive criteria. */
function JoinSelector({ value, onChange, compact = false }) {
    const isOr = (value || "and") === "or";
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                py: compact ? 0.25 : 0.75,
                pl: compact ? 0 : 0.5,
            }}
        >
            <TextField
                select
                size="small"
                value={value || "and"}
                onChange={(e) => onChange(e.target.value)}
                sx={{
                    ...fieldSx,
                    minWidth: 88,
                    "& .MuiInputBase-root": {
                        fontWeight: 700,
                        bgcolor: isOr ? "#fff7ed" : "#ecfdf5",
                        color: isOr ? "#c2410c" : "#047857",
                    },
                }}
                SelectProps={{ MenuProps: selectMenuProps }}
            >
                <MenuItem value="and">ET</MenuItem>
                <MenuItem value="or">OU</MenuItem>
            </TextField>
        </Box>
    );
}

/** Insert an item at a given index without mutating the source list. */
const insertAt = (list, idx, item) => {
    const next = [...(list || [])];
    next.splice(Math.max(0, Math.min(idx, next.length)), 0, item);
    return next;
};

/** Move an item between positions; returns the list unchanged if out of bounds. */
const moveItem = (list, from, to) => {
    const next = [...(list || [])];
    if (to < 0 || to >= next.length || from < 0 || from >= next.length) return next;
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

/**
 * Per-row action cluster: insert below (condition or group), reorder, delete.
 * Lets users add criteria anywhere in the tree instead of only at the end.
 */
function RowActions({
    onInsertAfter, onMoveUp, onMoveDown, canMoveUp, canMoveDown, onRemove,
    removeTooltip = "Supprimer",
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const closeMenu = () => setAnchorEl(null);

    return (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {onInsertAfter && (
                <>
                    <Tooltip title="Insérer un critère ici">
                        <IconButton
                            size="small"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{ color: "#007B8A" }}
                        >
                            <AddCircleOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={closeMenu}
                        disableScrollLock
                        sx={{ zIndex: 1700 }}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <MenuItem
                            onClick={() => { onInsertAfter("condition", "and"); closeMenu(); }}
                            sx={{ fontSize: "0.875rem" }}
                        >
                            <ListItemIcon><AddCircleOutlineIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
                                Insérer une condition (ET)
                            </ListItemText>
                        </MenuItem>
                        <MenuItem
                            onClick={() => { onInsertAfter("condition", "or"); closeMenu(); }}
                            sx={{ fontSize: "0.875rem" }}
                        >
                            <ListItemIcon><AddCircleOutlineIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
                                Insérer une condition (OU)
                            </ListItemText>
                        </MenuItem>
                        <MenuItem
                            onClick={() => { onInsertAfter("group"); closeMenu(); }}
                            sx={{ fontSize: "0.875rem" }}
                        >
                            <ListItemIcon><AccountTreeIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
                                Insérer un sous-groupe
                            </ListItemText>
                        </MenuItem>
                    </Menu>
                </>
            )}
            <Tooltip title="Monter">
                <span>
                    <IconButton size="small" disabled={!canMoveUp} onClick={onMoveUp}>
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Descendre">
                <span>
                    <IconButton size="small" disabled={!canMoveDown} onClick={onMoveDown}>
                        <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title={removeTooltip}>
                <IconButton size="small" onClick={onRemove} sx={{ color: "#EF4444" }}>
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

function ConditionRow({
    row, fields, onChange, onRemove, depth = 0,
    onInsertAfter, onMoveUp, onMoveDown, canMoveUp = false, canMoveDown = false,
}) {
    if (row.type === "group") {
        const children = row.conditions || [];
        const setChildren = (next) => onChange({ ...row, conditions: reconcileRowJoins(next) });
        const { level, accent, bg } = groupDepthStyle(depth);

        return (
            <Box
                sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${accent}30`,
                    borderLeft: `4px solid ${accent}`,
                    bgcolor: bg,
                    p: 2,
                    position: "relative",
                    boxShadow: `0 1px 4px ${accent}18`,
                }}
            >
                {level > 0 && (
                    <Typography
                        sx={{
                            position: "absolute",
                            top: 6,
                            right: 10,
                            fontSize: 9,
                            fontWeight: 700,
                            color: `${accent}80`,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                        }}
                    >
                        Niveau {level + 1}
                    </Typography>
                )}
                <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                    <FolderOpenIcon sx={{ fontSize: 18, color: accent }} />
                    <Chip
                        label={level === 0 ? "Groupe" : "Sous-groupe"}
                        size="small"
                        sx={{ fontWeight: 700, bgcolor: "#fff", color: accent, border: `1px solid ${accent}40` }}
                    />
                    <Button
                        size="small"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => {
                            const item = makeCondition(fields);
                            if (children.length > 0) item.joinWith = "and";
                            setChildren([...children, item]);
                        }}
                        sx={{ textTransform: "none", color: accent, bgcolor: "#fff", border: `1px solid ${accent}30`, "&:hover": { bgcolor: "#fff", borderColor: accent } }}
                    >
                        Ajouter dans le groupe
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <RowActions
                        onInsertAfter={onInsertAfter}
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                        canMoveUp={canMoveUp}
                        canMoveDown={canMoveDown}
                        onRemove={onRemove}
                        removeTooltip="Supprimer le groupe"
                    />
                </Box>
                <Box sx={{ pl: 1, borderLeft: `2px solid ${accent}25` }}>
                    {children.length === 0 ? (
                        <Typography fontSize={12} color="text.secondary" textAlign="center" py={2} fontStyle="italic">
                            Ce groupe est vide. Ajoutez des conditions ou sous-groupes.
                        </Typography>
                    ) : (
                        children.map((child, idx) => (
                            <React.Fragment key={child.id || idx}>
                                {idx > 0 && (
                                    <JoinSelector
                                        compact
                                        value={child.joinWith || "and"}
                                        onChange={(join) => {
                                            const next = [...children];
                                            next[idx] = { ...child, joinWith: join };
                                            setChildren(next);
                                        }}
                                    />
                                )}
                                <ConditionRow
                                    row={child}
                                    fields={fields}
                                    depth={depth + 1}
                                    onChange={(updated) => {
                                        const next = [...children];
                                        next[idx] = updated;
                                        setChildren(next);
                                    }}
                                    onRemove={() => setChildren(children.filter((_, i) => i !== idx))}
                                    onInsertAfter={(kind, joinWith) => {
                                        const item = kind === "group" ? makeGroup(fields) : makeCondition(fields);
                                        item.joinWith = joinWith || "and";
                                        setChildren(insertAt(children, idx + 1, item));
                                    }}
                                    onMoveUp={() => setChildren(reconcileRowJoins(moveItem(children, idx, idx - 1)))}
                                    onMoveDown={() => setChildren(reconcileRowJoins(moveItem(children, idx, idx + 1)))}
                                    canMoveUp={idx > 0}
                                    canMoveDown={idx < children.length - 1}
                                />
                            </React.Fragment>
                        ))
                    )}
                </Box>
            </Box>
        );
    }

    const propertyId = normalizePropertyId(row.property || "");
    const fieldMeta = fields.find((f) => f.id === propertyId);
    const isUnknownProperty = Boolean(propertyId && !isKnownProperty(propertyId));

    const isNumeric = fieldMeta?.type === "number";
    const isDate = fieldMeta?.type === "date";
    const isBoolean = fieldMeta?.type === "boolean";
    const noValue = row.operator === "exists";
    const isBetween = row.operator === "between" && !isBoolean;

    const betweenPair = Array.isArray(row.value) ? row.value : ["", ""];
    const setBetween = (idx, v) => {
        const next = Array.isArray(row.value) ? [...row.value] : ["", ""];
        next[idx] = v;
        onChange({ ...row, value: next });
    };
    const valueInputType = isDate ? "date" : isNumeric ? "number" : "text";

    return (
        <Box
            display="flex"
            gap={1}
            alignItems="flex-start"
            flexWrap="wrap"
            mb={1}
            sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#fff",
                border: "1px solid #e2e8f0",
                borderLeft: depth > 0 ? "3px solid #cbd5e1" : "3px solid #007B8A",
            }}
        >
            <Box sx={{ minWidth: 200, flex: 1 }}>
                <Autocomplete
                    size="small"
                    fullWidth
                    disableClearable
                    autoHighlight
                    options={fields}
                    getOptionLabel={(o) => o?.label || ""}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    value={fields.find((f) => f.id === propertyId) || fields[0] || null}
                    onChange={(_, newVal) => newVal && onChange({ ...row, property: newVal.id })}
                    noOptionsText="Aucune propriété disponible"
                    renderInput={(params) => (
                        <TextField {...params} label="Propriété" placeholder="Rechercher une propriété…" />
                    )}
                    sx={fieldSx}
                    ListboxProps={{ style: { maxHeight: 320 } }}
                    slotProps={{ paper: { sx: { zIndex: 1600 } } }}
                />
                {isUnknownProperty && (
                    <FormHelperText error sx={{ mx: 1.75 }}>
                        « {propertyId.replace("properties.", "")} » ignorée (absente du catalogue)
                    </FormHelperText>
                )}
            </Box>
            <TextField
                select size="small" label="Opérateur"
                value={row.operator || "equals"}
                onChange={(e) => {
                    const nextOp = e.target.value;
                    let nextValue = row.value;
                    if (nextOp === "between" && !Array.isArray(row.value)) {
                        nextValue = [row.value ?? "", ""];
                    } else if (nextOp !== "between" && Array.isArray(row.value)) {
                        nextValue = row.value[0] ?? "";
                    }
                    onChange({ ...row, operator: nextOp, value: nextValue });
                }}
                sx={{ ...fieldSx, minWidth: 160 }}
                SelectProps={{ MenuProps: selectMenuProps }}
            >
                {UNOMI_COMPARISON_OPERATORS.map((op) => (
                    <MenuItem key={op.id} value={op.id}>{op.label}</MenuItem>
                ))}
            </TextField>
            {!noValue && (
                isBoolean ? (
                    <TextField
                        select size="small" label="Valeur"
                        value={row.value === true || row.value === "true" ? "true" : "false"}
                        onChange={(e) => onChange({ ...row, value: e.target.value })}
                        sx={{ ...fieldSx, minWidth: 140, flex: 1 }}
                        SelectProps={{ MenuProps: selectMenuProps }}
                    >
                        <MenuItem value="true">Vrai</MenuItem>
                        <MenuItem value="false">Faux</MenuItem>
                    </TextField>
                ) : isBetween ? (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1, minWidth: 260 }}>
                        <TextField
                            size="small" label="De" type={valueInputType}
                            value={isDate ? toDateInputValue(betweenPair[0]) : (betweenPair[0] ?? "")}
                            onChange={(e) => setBetween(0, e.target.value)}
                            InputLabelProps={isDate ? { shrink: true } : undefined}
                            sx={{ ...fieldSx, flex: 1, minWidth: 120 }}
                        />
                        <Typography variant="body2" color="text.secondary">et</Typography>
                        <TextField
                            size="small" label="À" type={valueInputType}
                            value={isDate ? toDateInputValue(betweenPair[1]) : (betweenPair[1] ?? "")}
                            onChange={(e) => setBetween(1, e.target.value)}
                            InputLabelProps={isDate ? { shrink: true } : undefined}
                            sx={{ ...fieldSx, flex: 1, minWidth: 120 }}
                        />
                    </Box>
                ) : isDate ? (
                    <TextField
                        size="small" label="Valeur" type="date"
                        value={toDateInputValue(row.value)}
                        onChange={(e) => onChange({ ...row, value: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...fieldSx, minWidth: 160, flex: 1 }}
                    />
                ) : (
                    <TextField
                        size="small" label="Valeur"
                        type={isNumeric ? "number" : "text"}
                        value={row.value ?? ""}
                        onChange={(e) => onChange({ ...row, value: e.target.value })}
                        sx={{ ...fieldSx, minWidth: 140, flex: 1 }}
                    />
                )
            )}
            <Box sx={{ mt: 0.5 }}>
                <RowActions
                    onInsertAfter={onInsertAfter}
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    canMoveUp={canMoveUp}
                    canMoveDown={canMoveDown}
                    onRemove={onRemove}
                    removeTooltip="Supprimer la condition"
                />
            </Box>
        </Box>
    );
}

/**
 * Visual builder for Unomi profilePropertyCondition trees (brand injected server-side).
 */
const UnomiConditionBuilder = ({ rows, onChange }) => {
    const [fields, setFields] = useState(DEFAULT_UNOMI_PROPERTY_FIELDS);

    // Top-level criteria use per-row ET/OU join selectors. The brand filter is AND-ed
    // with this tree server-side, so the user cannot widen the segment beyond their brand.
    const loadFields = useCallback(async () => {
        try {
            const items = await segmentService.getConditionFields();
            if (Array.isArray(items) && items.length > 0) {
                setFields(items);
            }
        } catch {
            // Built-in catalog is already shown; API may be unavailable on some environments.
        }
    }, []);

    useEffect(() => {
        loadFields();
    }, [loadFields]);

    const emitChange = (nextRows) => {
        onChange({ combinator: "and", rows: reconcileRowJoins(nextRows) });
    };

    const updateRow = (idx, updated) => {
        const next = [...rows];
        next[idx] = updated;
        emitChange(next);
    };

    const removeRow = (idx) => {
        emitChange(rows.filter((_, i) => i !== idx));
    };

    const addCondition = (joinWith = "and") => {
        const item = makeCondition(fields);
        if ((rows || []).length > 0) item.joinWith = joinWith;
        emitChange([...(rows || []), item]);
    };

    const addGroup = () => {
        const item = makeGroup(fields);
        if ((rows || []).length > 0) item.joinWith = "and";
        emitChange([...(rows || []), item]);
    };

    /** Insert a new criterion/group right after the given row, keeping the rest intact. */
    const insertRowAfter = (idx, kind, joinWith = "and") => {
        const item = kind === "group" ? makeGroup(fields) : makeCondition(fields);
        item.joinWith = joinWith;
        emitChange(insertAt(rows, idx + 1, item));
    };

    const moveRow = (from, to) => {
        emitChange(moveItem(rows, from, to));
    };

    return (
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
            {!(rows || []).length && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    py={3}
                    fontStyle="italic"
                >
                    Aucune condition — ajoutez un critère pour définir le segment.
                </Typography>
            )}
            {(rows || []).map((row, idx) => (
                <React.Fragment key={row.id || idx}>
                    {idx > 0 && (
                        <JoinSelector
                            value={row.joinWith || "and"}
                            onChange={(join) => updateRow(idx, { ...row, joinWith: join })}
                        />
                    )}
                    <ConditionRow
                        row={row}
                        fields={fields}
                        onChange={(updated) => updateRow(idx, updated)}
                        onRemove={() => removeRow(idx)}
                        onInsertAfter={(kind, joinWith) => insertRowAfter(idx, kind, joinWith)}
                        onMoveUp={() => moveRow(idx, idx - 1)}
                        onMoveDown={() => moveRow(idx, idx + 1)}
                        canMoveUp={idx > 0}
                        canMoveDown={idx < (rows || []).length - 1}
                    />
                </React.Fragment>
            ))}

            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => addCondition("and")} sx={{ textTransform: "none" }}>
                    Ajouter condition (ET)
                </Button>
                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => addCondition("or")} sx={{ textTransform: "none" }}>
                    Ajouter condition (OU)
                </Button>
                <Button size="small" variant="outlined" onClick={addGroup} sx={{ textTransform: "none" }}>
                    Ajouter un sous-groupe
                </Button>
            </Box>
        </Box>
    );
};

export default UnomiConditionBuilder;
export { buildUnomiConditionFromRows, defaultEmptyRows };
